import * as React from 'react';

import getSymbolFromCurrency from 'currency-symbol-map';
import ReactCountryFlag from 'react-country-flag';
import TextareaAutosize from 'react-textarea-autosize';

import { Alert, Card, Input, Select, SelectOption } from '@components/common';
import { RadioGroup, RadioGroupOption } from '@components/common/RadioGroup';
import { getChangeFn, toPositiveNumber } from '@components/utils';
import { useDisabledFeatures } from '@hooks/useDisabledFeatures';
import { usePlan } from '@hooks/usePlan';
import { StepHelper, StepTitle } from 'components/StudiesApp/components/StudyDraft/pages/shared';

export const FormGroup: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <div className='mb-6'>{children}</div>
);
export const Label: React.FC<React.PropsWithChildren<{ htmlFor?: string }>> = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className='h500-bold mb-2 block'>
    {children}
  </label>
);
export const Helper: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <span className='h400 -mt-1 mb-3 block text-gray-500'>{children}</span>
);

export const REWARD_PRODUCT_ICONS = {
  visa: 'data:image/png;base64,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',
  mastercard:
    'data:image/png;base64,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',
  amazon:
    'data:image/png;base64,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',
  itunes: 'https://giftrocket-s3.imgix.net/Brands/US/iTunes/Digital/B2B/iTunes_B2B.png'
};

export const INCENTIVE_OPTIONS: SelectOption<IncentiveMethod | '' | 'money'>[] = [
  { label: 'No incentive', value: '' },
  { label: 'Money or gift card', value: 'money' },
  { label: 'Coupon code', value: 'coupon' },
  { label: 'Product gift', value: 'product' },
  { label: 'Other', value: 'other' }
  // {label: 'No incentive', value: ''},
];

export const INCENTIVE_FIELDS = {
  manual: ['amount', 'on_gq'],
  tremendous: ['amount', 'on_gq'],
  coupon: ['title', 'coupons', 'instructions'],
  product: ['title', 'instructions'],
  other: ['title', 'instructions']
};

export const INCENTIVE_TITLE_PLACEHOLDERS = {
  coupon: 'e.g. 50% off your next purchase',
  product: 'e.g. 1 month free using our service',
  other: 'e.g. coffee mug with our logo on it'
};

export const INCENTIVE_METHOD_OPTIONS: RadioGroupOption[] = [
  {
    value: 'tremendous',
    label: 'Through Great Question',
    className: 'xx-gq-incentives',
    helper: 'The participant can choose their incentive type via Tremendous.',
    extra: (
      <div className='mt-2 flex items-center space-x-2'>
        {Object.values(REWARD_PRODUCT_ICONS).map((src) => (
          <img alt='' key={src} src={src} width='50' height='30' />
        ))}
        <span className='h400 pl-1 font-normal text-gray-500'>and 859 other ways.</span>
      </div>
    )
  },
  {
    value: 'manual',
    label: 'Outside of Great Question',
    className: 'xx-manual-incentives',
    helper: 'You’ll process the incentive payment separately.'
  }
];

export const CURRENCY_OPTIONS = [
  { code: 'AUD', label: 'Australian Dollar', countryCode: 'AU' },
  { code: 'GBP', label: 'British Pound', countryCode: 'GB' },
  { code: 'CAD', label: 'Canadian Dollar', countryCode: 'CA' },
  { code: 'EUR', label: 'Euro', countryCode: 'EU' },
  { code: 'JPY', label: 'Japanese Yen', countryCode: 'JP' },
  // {code: 'CHF', label: 'Swiss Franc', countryCode: 'CH'},
  { code: 'TRY', label: 'Turkish Lira', countryCode: 'TR' },
  { code: 'USD', label: 'United States', countryCode: 'US' }
];

interface TextAreaProps {
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  minRows?: number;
  maxRows?: number;
  name?: string;
}

const TextArea: React.FC<React.PropsWithChildren<TextAreaProps>> = ({
  value,
  onChange,
  placeholder,
  minRows = 4,
  maxRows,
  name
}) => {
  function handleChange(e: React.FormEvent<HTMLTextAreaElement>) {
    onChange(e.currentTarget.value);
  }
  return (
    <TextareaAutosize
      name={name}
      minRows={minRows}
      maxRows={maxRows}
      placeholder={placeholder}
      onChange={handleChange}
      className='h400 block w-full rounded-md border-gray-200 p-3 placeholder-gray-400 transition duration-150 ease-in-out focus:border-indigo-500 focus:ring-indigo-500'
      value={value || ''}
    />
  );
};

interface InputWithDropdownProps {
  amount: number;
  setAmount: (amount: number) => void;
  disableCurrency?: boolean;
  currency: Study['currency']; // We
  setCurrency: (currency: Study['currency']) => void;
  id?: string;
}
const InputWithDropdown: React.FC<React.PropsWithChildren<InputWithDropdownProps>> = ({
  amount,
  setAmount,
  disableCurrency,
  currency,
  setCurrency,
  id
}) => {
  const symbol = getSymbolFromCurrency(currency) || '$';
  const currencyOptions = CURRENCY_OPTIONS.map(({ code, label, countryCode }) => ({
    label,
    value: code,
    data: { countryCode }
  }));

  return (
    <div>
      <div className='border-200 relative mt-1 rounded-md'>
        <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
          <span className='h400 text-gray-500'>{symbol}</span>
        </div>
        <Input
          id={id}
          type='number'
          name='incentive_amount'
          value={amount ? amount.toString() : undefined}
          onChange={(v) => setAmount(toPositiveNumber(v))}
          className='h400 no_arrows block w-full rounded-md border-gray-300 pl-7 pr-12 focus:border-indigo-500 focus:ring-indigo-500'
        />
        <div className='absolute inset-y-0 right-0 flex items-center'>
          {disableCurrency ? (
            <div className='px-4 py-2.5 text-sm'>{currency}</div>
          ) : (
            <Select
              noBorder
              syncWidths={false}
              disabled={disableCurrency}
              ulClassName='w-60 right-0'
              value={currency}
              onChange={setCurrency}
              options={currencyOptions}
              renderSelected={(option) => option.value}
              popperOptions={{ placement: 'bottom-end' }}
              renderLabel={(option) => (
                <span className='h600 flex flex-row items-center'>
                  <ReactCountryFlag countryCode={option.data?.countryCode} className='flex-0' />
                  <span className='h400 ml-3 flex-1'> {option.label}</span>
                  <span className='h400 font-bold text-gray-500 group-hover:text-white'>
                    {getSymbolFromCurrency(option.value)} {option.value}
                  </span>
                </span>
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
};
export const IncentiveForm: React.FC<React.PropsWithChildren<{ study: Study; onSave: (study: Study) => void }>> = ({
  study,
  onSave
}) => {
  // const [hasIncentive, setHasIncentive] = useState<boolean>(!!study.incentive_method)
  const method = study.incentive_method;
  const fields = method ? INCENTIVE_FIELDS[method] : [];

  const { hasFeature } = usePlan();
  const hasIncentives = hasFeature('incentives');

  const { disabledFeatures } = useDisabledFeatures();

  function handleIncentiveMethodChange(value) {
    if (value === 'money') {
      change?.('incentive_method', 'manual');
    } else {
      change?.('incentive_method', value);
    }
  }
  const change = getChangeFn<Study>(study, onSave);

  const isMoney = ['tremendous', 'manual'].includes(method || '');

  return (
    <>
      <FormGroup>
        <div className='h500-bold mb-2 block'>What type of incentive?</div>
        <Helper>Select how you want to reward participants.</Helper>
        <div className='flex flex-col'>
          <div className='w-60'>
            <Select
              overflowClass='visible'
              options={INCENTIVE_OPTIONS}
              onChange={handleIncentiveMethodChange}
              value={isMoney ? 'money' : method || ''}
            />
          </div>
        </div>
      </FormGroup>

      {fields.includes('amount') && (
        <FormGroup>
          <Label htmlFor='incentive_amount'>How much will the incentive be for participating?</Label>
          <Helper>This is the value the participant will be able to redeem.</Helper>
          <div className='w-60'>
            <InputWithDropdown
              id='incentive_amount'
              amount={study.incentive || 0}
              currency={study.currency}
              disableCurrency={study.state !== 'draft'}
              setCurrency={(v) => change?.('currency', v)}
              setAmount={(v) => change?.('incentive', v)}
            />
          </div>
        </FormGroup>
      )}

      {fields.includes('title') && (
        <FormGroup>
          <Label htmlFor='incentive_title'>Incentive title</Label>
          <Helper>This will be displayed to participants.</Helper>
          <Input
            placeholder={method ? INCENTIVE_TITLE_PLACEHOLDERS[method] : ''}
            onChange={(v) => change?.('incentive_title', v)}
            value={study.incentive_title || ''}
            size='2xl'
            name='incentive_title'
          />
        </FormGroup>
      )}
      {fields.includes('coupons') && (
        <FormGroup>
          <Label htmlFor='incentive_coupons'>Coupon codes</Label>
          <Helper>Include 1 code per person to be sent out after participating.</Helper>
          <TextArea
            placeholder='Enter coupons separated by commas or new lines.'
            maxRows={6}
            onChange={(v) => change?.('incentive_coupons', v)}
            value={study.incentive_coupons}
            name='incentive_coupons'
          />
        </FormGroup>
      )}
      {fields.includes('instructions') && (
        <FormGroup>
          <Label htmlFor='incentive_instructions'>Redemption instructions</Label>
          <Helper>Include details or links that participants will need to redeem their incentive.</Helper>
          <TextArea
            placeholder='Add instructions or links.'
            maxRows={6}
            onChange={(v) => change?.('incentive_instructions', v)}
            value={study.incentive_instructions}
            name='incentive_instructions'
          />
        </FormGroup>
      )}

      {fields.includes('on_gq') && (
        <FormGroup>
          <Label>How will you pay out the incentive?</Label>
          <RadioGroup
            options={INCENTIVE_METHOD_OPTIONS}
            selected={method || ''}
            disabled={!hasIncentives || disabledFeatures.incentives}
            onChange={(v) => change?.('incentive_method', v as Study['incentive_method'])}
          />
          {!hasIncentives && (
            <Alert type='warning' className='mt-4'>
              Paying incentives through Great Question is only available on paid accounts.
              <a className='font-bold text-gray-700 underline' href='/plans'>
                View plans
              </a>
            </Alert>
          )}
        </FormGroup>
      )}
    </>
  );
};

export const Incentives: React.FC<React.PropsWithChildren<PageProps>> = ({ study, onSave }) => {
  return (
    <div className='pt-gutter'>
      <div className='text-center'>
        <StepTitle>Set up your incentives</StepTitle>
        <StepHelper>Set the reward that people will get for participating.</StepHelper>
      </div>
      <Card className='mx-auto max-w-3xl px-12 py-8'>
        <IncentiveForm study={study} onSave={onSave} />
      </Card>
    </div>
  );
};
